import { HelpTooltip } from '@front/components/HelpTooltip';
import { useSourceSystem } from '@front/hook/Api/SourceSystem';
import { usePermissions } from '@front/hook/usePermissions';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFrame,
  DialogHeader,
  Loader,
  useToast,
} from '@plaidev/sour-react';
import { createFileRoute, type ErrorComponentProps, useNavigate } from '@tanstack/react-router';
import { useProject } from 'auth-lib-front';
import { useState } from 'react';
import { DomainV2 } from 'troppus-common/domain';
import { SourceSystemEditSheet } from './SourceSystemEditSheet';
import { type DeleteTarget, SourceSystemList } from './SourceSystemList';

import SourceSystem = DomainV2.Import.SourceSystem;

export const Route = createFileRoute('/settings/source-systems/')({
  component: SourceSystemListPage,
  errorComponent: SourceSystemsRouteError,
});

function SourceSystemsRouteError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'データの取得に失敗しました';
  return <div className="rounded-lg border border-negative bg-negative-light p-4 text-sm text-negative">{message}</div>;
}

/**
 * @feature ソースシステム一覧
 * @capabilities ソースシステム新規作成への遷移, 編集シート表示, 削除, テーブル定義追加への遷移
 * @behavior 「新規作成」→作成画面へ遷移, 行クリック→編集シート表示, ⋯メニュー「削除」→確認ダイアログ→削除実行, 「テーブル定義を追加」→テーブル定義作成画面へ遷移。新規作成・削除はプロジェクト管理者のみ
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
function SourceSystemListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { project } = useProject();
  const { isProjectAdmin } = usePermissions();
  const { data, isPending, isFetching } = useSourceSystem.List(project.id);
  const deleteMutation = useSourceSystem.Delete();

  const [editingId, setEditingId] = useState<SourceSystem.Id | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ projectId: project.id, id: deleteTarget.id });
      toast.success('削除しました');
      if (editingId === deleteTarget.id) setEditingId(null);
      setDeleteTarget(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : '削除に失敗しました';
      toast.error(message);
    }
  };

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );
  }

  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="m-0 text-xl font-semibold leading-none text-default">ソースシステム</h1>
          <HelpTooltip
            content={
              <div className="flex max-w-xs flex-col gap-1 text-left text-xs">
                <p className="font-medium">ソースシステムとは？</p>
                <p>
                  顧客データの取り込み元となる外部システム（CRM、基幹システム、データウェアハウス、CSV
                  連携など）の登録情報です。データの出どころをここで管理することで、取り込んだデータがどのシステム由来なのかを区別し、テーブル定義やインポートタスクと紐づけられます。
                </p>
                <p>まずは連携したいシステムごとに 1 件ずつ登録してください。</p>
              </div>
            }
            side="bottom"
            ariaLabel="ソースシステムの説明"
          />
        </div>
        {isProjectAdmin && (
          <Button variant="primary" size="sm" onClick={() => void navigate({ to: '/settings/source-systems/new' })}>
            新規作成
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-default bg-subtle-light py-12 text-center text-sm text-muted-light">
          {isProjectAdmin ? 'ソースシステムはまだありません。「新規作成」から追加してください。' : 'ソースシステムはまだありません。'}
        </p>
      ) : (
        <SourceSystemList
          rows={rows}
          loading={isFetching}
          isProjectAdmin={isProjectAdmin}
          onEdit={setEditingId}
          onAddTableDefinition={(id) => void navigate({ to: '/table-definitions/new', search: { sourceSystemId: id } })}
          onDelete={setDeleteTarget}
        />
      )}

      <SourceSystemEditSheet
        sourceSystemId={editingId}
        open={editingId !== null}
        onOpenChange={(open) => {
          if (!open) setEditingId(null);
        }}
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogFrame>
            <DialogHeader title="ソースシステムを削除" />
            <DialogBody>
              <p className="text-sm text-default">
                「{deleteTarget?.displayName}」を削除します。この操作は取り消せません。よろしいですか？
              </p>
            </DialogBody>
            <DialogActions>
              <DialogClose>
                <Button variant="basic">キャンセル</Button>
              </DialogClose>
              <Button variant="negative" loading={deleteMutation.isPending} onClick={() => void handleDelete()}>
                削除する
              </Button>
            </DialogActions>
          </DialogFrame>
        </DialogContent>
      </Dialog>
    </div>
  );
}
